import { c as _c } from "react/compiler-runtime";
import React, { PropsWithChildren, useCallback, useEffect, useRef } from "react";
import { SearchForm } from "../SearchForm";
import clsx from "classnames";
import { useTranslations } from "modules/i18n/hooks/useTranslations";
import { NavItems } from "./NavItems";
import { RegionDropdown } from "./RegionDropdown";
import { ActionButtons } from "./ActionButtons";
import { isElementVisible } from "common/lib/isElementVisible";
import { X } from "lucide-react";
interface MobileMenuProps {
  isActive: boolean;
  onClose: () => void;
}
const FOCUSABLE_SELECTORS = 'a[href], button:not(:disabled), select:not(:disabled), textarea:not(:disabled), input[type="text"]:not(:disabled), input[type="radio"]:not(:disabled), input[type="checkbox"]:not(:disabled), input[type="search"]:not(:disabled)';
const getFocusableElements = (container: HTMLElement) => Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS)).filter(isElementVisible) as HTMLElement[];
const useFocusTrap = (isActive, containerRef, onClose) => {
  "use memo";

  const $ = _c(13);
  let t0;
  if ($[0] !== containerRef || $[1] !== isActive || $[2] !== onClose) {
    t0 = event => {
      if (!isActive || !containerRef.current) {
        return;
      }
      const focusableElements = getFocusableElements(containerRef.current);
      if (focusableElements.length === 0) {
        return;
      }
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.key === "Escape") {
        event.preventDefault();
        const searchInput = containerRef.current.querySelector("input[type=\"search\"]") as HTMLInputElement;
        if (searchInput && searchInput.value !== "" && document.activeElement === searchInput) {
          searchInput.value = "";
          searchInput.focus();
        } else {
          onClose();
        }
        return;
      }
      if (event.key !== "Tab") {
        return;
      }
      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      }
    };
    $[0] = containerRef;
    $[1] = isActive;
    $[2] = onClose;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const handleKeyDown = t0;
  let t1;
  if ($[4] !== containerRef || $[5] !== handleKeyDown || $[6] !== isActive) {
    t1 = () => {
      const menuElement = containerRef.current;
      if (!isActive || !menuElement) {
        return;
      }
      const timeoutId = setTimeout(() => {
        const focusableElements_0 = getFocusableElements(menuElement);
        if (focusableElements_0.length !== 0) {
          focusableElements_0[0].focus();
        }
      }, 50);
      menuElement.addEventListener("keydown", handleKeyDown);
      return () => {
        clearTimeout(timeoutId);
        if (handleKeyDown) {
          menuElement.removeEventListener("keydown", handleKeyDown);
        }
      };
    };
    $[4] = containerRef;
    $[5] = handleKeyDown;
    $[6] = isActive;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== containerRef || $[9] !== handleKeyDown || $[10] !== isActive || $[11] !== onClose) {
    t2 = [isActive, containerRef, onClose, handleKeyDown];
    $[8] = containerRef;
    $[9] = handleKeyDown;
    $[10] = isActive;
    $[11] = onClose;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  useEffect(t1, t2);
};

// Handles scroll lock & device rotation (normal reasonable methods don't work properly for Safari)
const useMobileScrollLock = (isActive, onClose) => {
  "use memo";

  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== isActive || $[1] !== onClose) {
    t0 = () => {
      const mediaQuery = window.matchMedia("(min-width: 992px)");
      const handleResize = e => {
        if (e.matches && isActive) {
          onClose();
        }
      };
      mediaQuery.addEventListener("change", handleResize);
      if (!isActive) {
        return () => mediaQuery.removeEventListener("change", handleResize);
      }
      const scrollY = window.scrollY;
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const originalWidth = document.body.style.width;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      return () => {
        mediaQuery.removeEventListener("change", handleResize);
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = originalWidth;
        window.scrollTo(0, scrollY);
      };
    };
    t1 = [isActive, onClose];
    $[0] = isActive;
    $[1] = onClose;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};
export const MobileMenu = t0 => {
  "use memo";

  const $ = _c(17);
  const {
    isActive,
    onClose
  } = t0;
  const t = useTranslations("layout.header");
  const menuRef = useRef(null);
  useFocusTrap(isActive, menuRef, onClose);
  useMobileScrollLock(isActive, onClose);
  const t1 = !isActive;
  let t2;
  if ($[0] !== isActive || $[1] !== t1) {
    t2 = clsx("absolute top-px mt-10 w-full bg-white text-gray-800 transition-all duration-500 lg:hidden", {
      "visible z-40 h-svh pb-8": isActive,
      "invisible h-0": t1
    });
    $[0] = isActive;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = isActive ? true : undefined;
  let t4;
  if ($[3] !== t) {
    t4 = t("menu");
    $[3] = t;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isActive || $[6] !== onClose || $[7] !== t) {
    t5 = isActive && <button type="button" onClick={onClose} aria-label={t("close")} className="-mt-10 block p-2 text-gray-800 -outline-offset-1 hover:bg-gray-50" aria-controls="mobile-menu-container" aria-expanded={true}><X className="size-6" /></button>;
    $[5] = isActive;
    $[6] = onClose;
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="h-full overflow-y-auto md:max-w-screen"><div className="mx-1"><SearchForm /></div><NavItems /><RegionDropdown /><ActionButtons /></div>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <div role="document" className="h-full">{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t2 || $[13] !== t3 || $[14] !== t4 || $[15] !== t7) {
    t8 = <section ref={menuRef} id="mobile-menu-container" className={t2} data-testid="mobileMenu" aria-modal={t3} aria-label={t4} role="dialog">{t7}</section>;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
};